/**
 * English Translations
 * ====================
 */

export const en = {
  // ═══════════════════════════════════════════════════════════
  // General
  // ═══════════════════════════════════════════════════════════
  general: {
    appName: 'Watrly',
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    update: 'Update',
    search: 'Search',
    filter: 'Filter',
    clear: 'Clear',
    reset: 'Reset',
    confirm: 'Confirm',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    continue: 'Continue',
    finish: 'Finish',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    all: 'All',
    none: 'None',
    select: 'Select',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    noResults: 'No results found',
    noData: 'No data available',
    required: 'Required',
    optional: 'Optional',
    more: 'More',
    less: 'Less',
    showMore: 'Show More',
    showLess: 'Show Less',
    viewAll: 'View All',
    details: 'Details',
    actions: 'Actions',
    status: 'Status',
    date: 'Date',
    time: 'Time',
    from: 'From',
    to: 'To',
    total: 'Total',
    items: 'items',
    page: 'Page',
    of: 'of',
    rows: 'rows',
    perPage: 'per page',
  },

  // ═══════════════════════════════════════════════════════════
  // Authentication
  // ═══════════════════════════════════════════════════════════
  auth: {
    login: 'Login',
    logout: 'Logout',
    register: 'Register',
    forgotPassword: 'Forgot Password?',
    resetPassword: 'Reset Password',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    rememberMe: 'Remember Me',
    loginSuccess: 'Login successful',
    logoutSuccess: 'Logout successful',
    invalidCredentials: 'Invalid credentials',
    sessionExpired: 'Session expired, please login again',
    welcomeBack: 'Welcome back',
    signInToContinue: 'Sign in to continue',
  },

  // ═══════════════════════════════════════════════════════════
  // Forms
  // ═══════════════════════════════════════════════════════════
  forms: {
    firstName: 'First Name',
    lastName: 'Last Name',
    fullName: 'Full Name',
    phone: 'Phone Number',
    address: 'Address',
    city: 'City',
    country: 'Country',
    postalCode: 'Postal Code',
    description: 'Description',
    notes: 'Notes',
    attachments: 'Attachments',
    uploadFile: 'Upload File',
    dragAndDrop: 'Drag and drop files here',
    or: 'or',
    browse: 'Browse',
    maxFileSize: 'Max file size',
    allowedTypes: 'Allowed types',
  },

  // ═══════════════════════════════════════════════════════════
  // Validation
  // ═══════════════════════════════════════════════════════════
  validation: {
    required: 'This field is required',
    email: 'Invalid email address',
    minLength: 'Must be at least {min} characters',
    maxLength: 'Must not exceed {max} characters',
    minValue: 'Must be at least {min}',
    maxValue: 'Must not exceed {max}',
    pattern: 'Invalid format',
    passwordMismatch: 'Passwords do not match',
    passwordStrength: 'Password is too weak',
    invalidPhone: 'Invalid phone number',
    invalidDate: 'Invalid date',
    futureDate: 'Date must be in the future',
    pastDate: 'Date must be in the past',
  },

  // ═══════════════════════════════════════════════════════════
  // Messages
  // ═══════════════════════════════════════════════════════════
  messages: {
    success: 'Operation completed successfully',
    error: 'An error occurred, please try again',
    warning: 'Warning',
    info: 'Information',
    confirmDelete: 'Are you sure you want to delete?',
    confirmAction: 'Are you sure you want to proceed?',
    unsavedChanges: 'You have unsaved changes',
    discardChanges: 'Discard changes?',
    savedSuccessfully: 'Saved successfully',
    deletedSuccessfully: 'Deleted successfully',
    updatedSuccessfully: 'Updated successfully',
    createdSuccessfully: 'Created successfully',
    operationFailed: 'Operation failed',
    networkError: 'Network error',
    serverError: 'Server error',
    unauthorized: 'Unauthorized action',
    forbidden: 'Access denied',
    notFound: 'Not found',
    timeout: 'Request timeout',
  },

  // ═══════════════════════════════════════════════════════════
  // Date & Time
  // ═══════════════════════════════════════════════════════════
  dateTime: {
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisYear: 'This Year',
    lastYear: 'Last Year',
    custom: 'Custom',
    startDate: 'Start Date',
    endDate: 'End Date',
    selectDate: 'Select Date',
    selectTime: 'Select Time',
    am: 'AM',
    pm: 'PM',
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    months: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    monthsShort: [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ],
  },

  // ═══════════════════════════════════════════════════════════
  // Tables
  // ═══════════════════════════════════════════════════════════
  table: {
    showing: 'Showing',
    entries: 'entries',
    noRecords: 'No records found',
    loading: 'Loading data...',
    sortAsc: 'Sort ascending',
    sortDesc: 'Sort descending',
    export: 'Export',
    print: 'Print',
    columns: 'Columns',
    refresh: 'Refresh',
  },

  // ═══════════════════════════════════════════════════════════
  // Orders
  // ═══════════════════════════════════════════════════════════
  orders: {
    order: 'Order',
    orders: 'Orders',
    newOrder: 'New Order',
    orderNumber: 'Order Number',
    orderDate: 'Order Date',
    orderStatus: 'Order Status',
    orderDetails: 'Order Details',
    quantity: 'Quantity',
    liters: 'liters',
    waterType: 'Water Type',
    drinkingWater: 'Drinking Water',
    groundWater: 'Ground Water',
    deliveryAddress: 'Delivery Address',
    scheduledTime: 'Scheduled Time',
    estimatedArrival: 'Estimated Arrival',
    price: 'Price',
    totalPrice: 'Total Price',
    paymentMethod: 'Payment Method',
    cash: 'Cash',
    card: 'Card',
    wallet: 'Wallet',
    pending: 'Pending',
    confirmed: 'Confirmed',
    assigned: 'Assigned',
    inProgress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  },

  // ═══════════════════════════════════════════════════════════
  // Sidebar
  // ═══════════════════════════════════════════════════════════
  sidebar: {
    dashboard: 'Dashboard',
    orders: 'Orders',
    customers: 'Customers',
    drivers: 'Drivers',
    suppliers: 'Suppliers',
    reports: 'Reports',
    settings: 'Settings',
    support: 'Support',
    users: 'Users',
    roles: 'Roles',
    notifications: 'Notifications',
    profile: 'Profile',
    billing: 'Billing',
    analytics: 'Analytics',
  },
};

export default en;
